import { useEffect, useState, type FormEvent } from 'react';
import axios from 'axios';
import {
  X,
  AlertTriangle,
  Loader2,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  CalendarClock,
  Send,
} from 'lucide-react';
import { forumsAPI, usersAPI } from '../api/client';
import DateTimeField from './DateTimeField';
import InviteQueuePanel from './InviteQueuePanel';
import {
  clearInviteDraft,
  loadInviteDraft,
  saveInviteDraft,
  type InviteDraftRow,
} from '../lib/inviteDraft';
import {
  localDateTimeValue,
  nextGridLocalDateTime,
  snapLocalDateTimeForward,
} from '../lib/sendAtGrid';
import type {
  BatchInviteResult,
  ForumType,
  InviteRowPayload,
  ScheduledInvite,
} from '../types';

type InviteRole = 'member' | 'advisor' | 'facilitator' | 'facilitator_admin' | 'admin' | 'guest';
type InviteSnapshotType = 'member' | 'advisor' | 'leader' | 'nonprofit';

function defaultSnapshotType(role: InviteRole): InviteSnapshotType {
  if (role === 'advisor') return 'advisor';
  if (role === 'facilitator' || role === 'facilitator_admin' || role === 'admin') return 'leader';
  return 'member';
}

interface ForumOption {
  id: number;
  name: string;
  city: string;
  state: string;
  forum_type: ForumType;
  monthly_price: string;
  advisor_monthly_price: string;
}

interface InviteRow {
  key: number;
  firstName: string;
  lastName: string;
  email: string;
  role: InviteRole;
  snapshotType: InviteSnapshotType;
  forumId: number | '';
  customAmount: string; // blank = the forum's standard rate
  guestExpiresAt: string;
  skipOnboarding: boolean;
  error: string | null;
}

let rowKeyCounter = 1;

function standardMonthlyAmount(
  forums: ForumOption[],
  forumId: number | '',
  role: InviteRole,
): string {
  if (forumId === '') return '';
  const forum = forums.find((f) => f.id === forumId);
  if (!forum) return '';
  const raw = role === 'advisor' ? forum.advisor_monthly_price : forum.monthly_price;
  const amount = Number(raw);
  return Number.isFinite(amount) ? amount.toFixed(2) : '';
}

function amountMatches(value: string, standard: string): boolean {
  if (value.trim() === '' || standard === '') return value.trim() === standard;
  return Number(value) === Number(standard);
}

function emptyRow(presetForumId?: number | null): InviteRow {
  return {
    key: rowKeyCounter++,
    firstName: '',
    lastName: '',
    email: '',
    role: 'member',
    snapshotType: 'member',
    forumId: presetForumId ?? '',
    customAmount: '',
    guestExpiresAt: localDateTimeValue(30 * 24 * 60),
    skipOnboarding: false,
    error: null,
  };
}

function rowFromDraft(draft: InviteDraftRow): InviteRow {
  return {
    ...emptyRow(),
    firstName: draft.firstName,
    lastName: draft.lastName,
    email: draft.email,
    role: draft.role as InviteRole,
    snapshotType: draft.snapshotType as InviteSnapshotType,
    forumId: draft.forumId,
    customAmount: draft.customAmount,
    guestExpiresAt: draft.guestExpiresAt || localDateTimeValue(30 * 24 * 60),
    skipOnboarding: draft.skipOnboarding,
  };
}

function rowToDraft(row: InviteRow): InviteDraftRow {
  return {
    firstName: row.firstName,
    lastName: row.lastName,
    email: row.email,
    role: row.role,
    snapshotType: row.snapshotType,
    forumId: row.forumId,
    customAmount: row.customAmount,
    guestExpiresAt: row.guestExpiresAt,
    skipOnboarding: row.skipOnboarding,
  };
}

function formatSendAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const CELL_INPUT =
  'w-full px-2 py-2 text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-gold/70 rounded';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  isFacilitator: boolean;
  /** Pre-select a forum (e.g. the one currently open in Forum Management). */
  presetForumId?: number | null;
  /** Called after a successful send/schedule so the parent can refresh / show a banner. */
  onInvited?: (message: string, forumId: number | '') => void;
}

/**
 * Shared "Invite to Iron Forums" modal (Forum page, Admin → Members).
 * A spreadsheet-style grid: one row per person, sent immediately or
 * scheduled onto the 10-minute send grid. The Invites tab lists queued
 * and awaiting-setup people with filters and bulk reschedule/cancel.
 */
export default function InviteMemberModal({
  isOpen,
  onClose,
  isAdmin,
  isFacilitator,
  presetForumId,
  onInvited,
}: InviteMemberModalProps) {
  const [forums, setForums] = useState<ForumOption[]>([]);
  const [forumsError, setForumsError] = useState(false);
  const [rows, setRows] = useState<InviteRow[]>([emptyRow(presetForumId)]);
  const [personalNote, setPersonalNote] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [sendAt, setSendAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<BatchInviteResult[] | null>(null);
  const [scheduled, setScheduled] = useState<ScheduledInvite[]>([]);
  const [tab, setTab] = useState<'invite' | 'invites'>('invite');
  const [draftRestored, setDraftRestored] = useState(false);
  // Gates the draft-save effect so the pristine initial state can't wipe a
  // stored draft before the open effect has had a chance to restore it.
  const [draftHydrated, setDraftHydrated] = useState(false);

  // A failed load must be visible and retryable: silently rendering an empty
  // forum dropdown looks like "you can't pick a forum here" (launch L17).
  const loadForums = async () => {
    setForumsError(false);
    try {
      const data = isAdmin
        ? await forumsAPI.getAll()
        : await forumsAPI.getFacilitating();
      setForums(
        data.map((f) => ({
          id: f.id,
          name: f.name,
          city: f.city,
          state: f.state,
          forum_type: f.forum_type,
          monthly_price: f.monthly_price,
          advisor_monthly_price: f.advisor_monthly_price,
        })),
      );
    } catch {
      setForums([]);
      setForumsError(true);
    }
  };

  // Each time the modal opens: restore any unsent draft (typed rows are too
  // expensive to lose — see Denny's 17 hand-entered invites), otherwise start
  // fresh. Also load forum options and any still-pending scheduled invites.
  useEffect(() => {
    if (!isOpen) return;
    const draft = loadInviteDraft();
    if (draft) {
      setRows(draft.rows.map(rowFromDraft));
      setPersonalNote(draft.personalNote);
      const sendAtStillFuture =
        draft.sendAt && new Date(draft.sendAt).getTime() > Date.now();
      setScheduleEnabled(draft.scheduleEnabled && Boolean(sendAtStillFuture));
      setSendAt(sendAtStillFuture ? snapLocalDateTimeForward(draft.sendAt) : nextGridLocalDateTime(60));
      setDraftRestored(true);
    } else {
      setRows([emptyRow(presetForumId)]);
      setPersonalNote('');
      setScheduleEnabled(false);
      setSendAt(nextGridLocalDateTime(60));
      setDraftRestored(false);
    }
    setDraftHydrated(true);
    setError(null);
    setResults(null);
    setTab('invite');

    let cancelled = false;
    const loadScheduled = async () => {
      try {
        // Admins audit every pending invite across forums; others see their own.
        const data = await usersAPI.getScheduledInvites(isAdmin);
        if (!cancelled) setScheduled(data);
      } catch {
        if (!cancelled) setScheduled([]);
      }
    };
    void loadForums();
    loadScheduled();
    return () => {
      cancelled = true;
      setDraftHydrated(false);
    };
  }, [isOpen, isAdmin, presetForumId]);

  // Continuously save the unsent grid so closing the modal (or the app
  // dying) never loses typed rows. An emptied-out grid clears the draft.
  useEffect(() => {
    if (!isOpen || !draftHydrated || results) return;
    saveInviteDraft({
      rows: rows.map(rowToDraft),
      personalNote,
      scheduleEnabled,
      sendAt,
    });
  }, [isOpen, draftHydrated, results, rows, personalNote, scheduleEnabled, sendAt]);

  useEffect(() => {
    if (forums.length === 0) return;
    setRows((prev) =>
      prev.map((row) => {
        if (row.customAmount !== '' || row.forumId === '') return row;
        return {
          ...row,
          customAmount: standardMonthlyAmount(forums, row.forumId, row.role),
        };
      }),
    );
  }, [forums]);

  if (!isOpen) return null;

  const updateRow = (key: number, patch: Partial<InviteRow>) => {
    setRows((prev) => prev.map((r) => {
      if (r.key !== key) return r;
      const next = { ...r, ...patch, error: null };
      if ('role' in patch && !('snapshotType' in patch)) {
        if (r.snapshotType === defaultSnapshotType(r.role)) {
          next.snapshotType = defaultSnapshotType(next.role);
        }
      }
      if ('role' in patch || 'forumId' in patch) {
        const previousStandard = standardMonthlyAmount(forums, r.forumId, r.role);
        const nextStandard = standardMonthlyAmount(forums, next.forumId, next.role);
        if (next.customAmount === '' || amountMatches(next.customAmount, previousStandard)) {
          next.customAmount = nextStandard;
        }
      }
      return next;
    }));
  };

  const addRow = () => setRows((prev) => {
    const row = emptyRow(presetForumId);
    row.customAmount = standardMonthlyAmount(forums, row.forumId, row.role);
    return [...prev, row];
  });

  const removeRow = (key: number) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

  const isBlankRow = (row: InviteRow) =>
    !row.email.trim() && !row.firstName.trim() && !row.lastName.trim();
  const filledRows = rows.filter((r) => !isBlankRow(r));
  const inviteCount = Math.max(filledRows.length, 1);

  /** Client-side checks mirroring the single-invite rules; returns the payloads or sets row errors. */
  const validateRows = (): InviteRowPayload[] | null => {
    const checked = rows.map((row) => {
      if (filledRows.length > 0 && isBlankRow(row)) {
        return { row: { ...row, error: null }, payload: null };
      }
      const effectiveRole: InviteRole = isAdmin
        ? row.role
        : row.role === 'advisor'
          ? 'advisor'
          : row.role === 'guest'
            ? 'guest'
            : 'member';
      // Admins can invite anyone without a forum (facilitators create their
      // own forum after joining); facilitators must pick one of theirs.
      // Guests always need a forum — they discover that chapter.
      const forumRequired = (isFacilitator && !isAdmin) || effectiveRole === 'guest';
      const standardAmount = standardMonthlyAmount(forums, row.forumId, effectiveRole);
      const wantsCustomAmount =
        row.customAmount.trim() !== '' &&
        !amountMatches(row.customAmount, standardAmount) &&
        (effectiveRole === 'member' || effectiveRole === 'advisor');
      let rowError: string | null = null;
      if (!row.email.trim()) {
        rowError = 'Email is required.';
      } else if (forumRequired && row.forumId === '') {
        rowError = 'Choose a Forum for the new member.';
      } else if (wantsCustomAmount) {
        const parsed = Number(row.customAmount);
        if (Number.isNaN(parsed) || parsed < 0) {
          rowError = 'Enter a valid custom amount ($0 or more).';
        }
      }
      const payload: InviteRowPayload = {
        email: row.email.trim(),
        first_name: row.firstName.trim(),
        last_name: row.lastName.trim(),
        role: effectiveRole,
        snapshot_type: row.snapshotType,
        forum_id: row.forumId === '' ? undefined : row.forumId,
        custom_monthly_amount: wantsCustomAmount
          ? Number(row.customAmount).toFixed(2)
          : undefined,
        personal_note: personalNote.trim() || undefined,
        guest_expires_at:
          effectiveRole === 'guest' && row.guestExpiresAt
            ? new Date(row.guestExpiresAt).toISOString()
            : undefined,
        skip_onboarding: effectiveRole === 'guest' ? row.skipOnboarding : undefined,
      };
      return { row: { ...row, error: rowError }, payload };
    });
    setRows(checked.map((c) => c.row));
    if (checked.some((c) => c.row.error)) return null;
    return checked.flatMap((c) => (c.payload ? [c.payload] : []));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const payloads = validateRows();
    if (!payloads) return;

    let sendAtIso: string | undefined;
    if (scheduleEnabled) {
      const snapped = snapLocalDateTimeForward(sendAt);
      const when = new Date(snapped);
      if (!sendAt || Number.isNaN(when.getTime())) {
        setError('Choose a valid date and time to send the invites.');
        return;
      }
      if (when.getTime() <= Date.now()) {
        setError('The scheduled send time must be in the future.');
        return;
      }
      sendAtIso = when.toISOString();
    }

    setSubmitting(true);
    try {
      const response = await usersAPI.inviteBatch({ invites: payloads, send_at: sendAtIso });
      setResults(response.results);
      const { sent, scheduled: scheduledCount, failed } = response.counts;
      if (failed === 0) {
        clearInviteDraft();
      } else {
        // Keep only what still needs attention: if they come back later,
        // restore just the rows that failed, not the ones already sent.
        const failedEmails = new Set(
          response.results
            .filter((r) => r.status === 'failed')
            .map((r) => r.email.toLowerCase()),
        );
        saveInviteDraft({
          rows: rows
            .filter((r) => failedEmails.has(r.email.trim().toLowerCase()))
            .map(rowToDraft),
          personalNote,
          scheduleEnabled,
          sendAt,
        });
      }
      if (sent + scheduledCount > 0) {
        const parts: string[] = [];
        if (sent > 0) parts.push(`${sent} invitation${sent === 1 ? '' : 's'} sent`);
        if (scheduledCount > 0) parts.push(`${scheduledCount} scheduled`);
        if (failed > 0) parts.push(`${failed} failed`);
        onInvited?.(`${parts.join(', ')}.`, rows[0]?.forumId ?? '');
      }
      if (scheduledCount > 0) {
        usersAPI.getScheduledInvites(isAdmin).then(setScheduled).catch(() => {});
      }
    } catch (err) {
      let detail = 'Failed to send invitations.';
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { error?: string } | undefined;
        detail = data?.error || detail;
      }
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  /** From the results screen: keep only the failed rows for fixing and retrying. */
  const editFailedRows = () => {
    if (!results) return;
    const failedEmails = new Set(
      results.filter((r) => r.status === 'failed').map((r) => r.email.toLowerCase()),
    );
    setRows((prev) => {
      const kept = prev.filter((r) => failedEmails.has(r.email.trim().toLowerCase()));
      return kept.length > 0 ? kept : [emptyRow(presetForumId)];
    });
    setResults(null);
  };

  const failedCount = results?.filter((r) => r.status === 'failed').length ?? 0;
  const rowErrors = rows
    .map((r, i) => (r.error ? { index: i + 1, message: r.error } : null))
    .filter((e): e is { index: number; message: string } => e !== null);
  const anyFacilitatorRole = rows.some(
    (r) => r.role === 'facilitator' || r.role === 'facilitator_admin',
  );

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center px-6 pt-6 pb-2">
          <h3 className="text-lg font-semibold">Invite to Iron Forums</h3>
          <button onClick={onClose} aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex gap-1 px-6 border-b border-gray-100">
          {(
            [
              { key: 'invite', label: 'Send invites', icon: Send },
              { key: 'invites', label: 'Invites', icon: CalendarClock },
            ] as const
          ).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                tab === key
                  ? 'border-gold text-navy'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              <Icon size={15} />
              {label}
              {key === 'invites' && scheduled.length > 0 && (
                <span className="min-w-[1.1rem] px-1 py-0.5 rounded-full bg-navy/10 text-navy text-[10px] font-semibold leading-none text-center">
                  {scheduled.length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="px-6 py-5 overflow-y-auto">
          {tab === 'invites' ? (
            <InviteQueuePanel
              isAdmin={isAdmin}
              forums={forums}
              scheduled={scheduled}
              setScheduled={setScheduled}
              presetForumId={presetForumId}
            />
          ) : results ? (
            /* ---- Results screen ---- */
            <div className="space-y-4">
              <div className="space-y-2">
                {results.map((r, i) => (
                  <div
                    key={i}
                    className={`rounded-lg px-4 py-3 text-sm flex items-start gap-2 ${
                      r.status === 'failed'
                        ? 'bg-red-50 text-red-700'
                        : r.status === 'scheduled'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-green-50 text-green-700'
                    }`}
                  >
                    {r.status === 'failed' ? (
                      <XCircle size={16} className="mt-0.5 flex-shrink-0" />
                    ) : r.status === 'scheduled' ? (
                      <Clock size={16} className="mt-0.5 flex-shrink-0" />
                    ) : (
                      <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p>
                        <span className="font-medium">{r.email}</span>
                        {' — '}
                        {r.status === 'scheduled' && r.send_at
                          ? `Scheduled for ${formatSendAt(r.send_at)}.`
                          : r.message}
                      </p>
                      {r.setup_url && (
                        <input
                          type="text"
                          readOnly
                          value={r.setup_url}
                          onFocus={(e) => e.currentTarget.select()}
                          aria-label={`Setup link for ${r.email}`}
                          className="mt-1.5 w-full px-2 py-1.5 border border-gray-300 rounded text-xs bg-white text-gray-700"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                {failedCount > 0 && (
                  <button
                    onClick={editFailedRows}
                    className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition"
                  >
                    Fix failed &amp; retry
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="flex-1 bg-navy text-white py-2.5 rounded-lg font-semibold hover:bg-navy-dark transition"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            /* ---- Spreadsheet-style entry grid ---- */
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-gray-500">
                One row per person — we'll create their accounts and email each a secure
                link to set their password and get started.
              </p>

              {draftRestored && (
                <div className="bg-blue-50 text-blue-700 px-4 py-3 rounded-lg text-sm flex items-start justify-between gap-3">
                  <span className="flex items-start gap-2">
                    <Clock size={16} className="mt-0.5 flex-shrink-0" />
                    <span>
                      We restored invites you typed earlier but hadn't sent.
                      Nothing has been sent yet.
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      clearInviteDraft();
                      setRows([emptyRow(presetForumId)]);
                      setPersonalNote('');
                      setScheduleEnabled(false);
                      setSendAt(nextGridLocalDateTime(60));
                      setDraftRestored(false);
                    }}
                    className="text-xs font-medium text-blue-700 underline whitespace-nowrap"
                  >
                    Start fresh
                  </button>
                </div>
              )}

              {forumsError && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <span>The forum list didn't load, so rows can't be assigned a forum yet.</span>
                  <button
                    type="button"
                    onClick={() => void loadForums()}
                    className="shrink-0 font-semibold text-navy hover:underline"
                  >
                    Retry
                  </button>
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-x-auto">
                <table className="w-full min-w-[56rem]">
                  <thead>
                    <tr className="bg-off-white text-left">
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider w-16 text-center">#</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">First Name</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Last Name</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider min-w-64">
                        Email <span className="text-red-500">*</span>
                      </th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {isAdmin ? 'Role' : 'Type'}
                      </th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Snapshot
                      </th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Forum{isFacilitator && !isAdmin ? <span className="text-red-500"> *</span> : ''}
                      </th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider w-36" title="Custom monthly amount, or guest expiry">
                        $ / expires
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row, index) => (
                      <tr key={row.key} className={row.error ? 'bg-red-50/60' : undefined}>
                        <td className="px-1 py-1">
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-xs text-gray-400 w-4 text-right">{index + 1}</span>
                            {rows.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRow(row.key)}
                                aria-label={`Remove row ${index + 1}`}
                                title="Remove this row"
                                className="p-1.5 rounded-md text-red-500 hover:bg-red-50 hover:text-red-600 transition"
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-1 py-1 border-l border-gray-100">
                          <input
                            type="text"
                            value={row.firstName}
                            onChange={(e) => updateRow(row.key, { firstName: e.target.value })}
                            aria-label={`Row ${index + 1} first name`}
                            className={CELL_INPUT}
                          />
                        </td>
                        <td className="px-1 py-1 border-l border-gray-100">
                          <input
                            type="text"
                            value={row.lastName}
                            onChange={(e) => updateRow(row.key, { lastName: e.target.value })}
                            aria-label={`Row ${index + 1} last name`}
                            className={CELL_INPUT}
                          />
                        </td>
                        <td className="px-1 py-1 border-l border-gray-100 min-w-64">
                          <input
                            type="email"
                            value={row.email}
                            onChange={(e) => updateRow(row.key, { email: e.target.value })}
                            placeholder="member@example.com"
                            required={filledRows.length === 0 || !isBlankRow(row)}
                            aria-label={`Row ${index + 1} email`}
                            className={`${CELL_INPUT} min-w-64`}
                          />
                        </td>
                        <td className="px-1 py-1 border-l border-gray-100">
                          {isAdmin ? (
                            <select
                              value={row.role}
                              onChange={(e) => updateRow(row.key, { role: e.target.value as InviteRole })}
                              aria-label={`Row ${index + 1} role`}
                              className={`${CELL_INPUT} bg-white`}
                            >
                              <option value="member">Member</option>
                              <option value="advisor">Advisor</option>
                              <option value="guest">Guest</option>
                              <option value="facilitator">Facilitator</option>
                              <option value="facilitator_admin">Facilitator + Admin</option>
                              <option value="admin">Admin</option>
                            </select>
                          ) : (
                            <select
                              value={
                                row.role === 'advisor' || row.role === 'guest' ? row.role : 'member'
                              }
                              onChange={(e) => updateRow(row.key, { role: e.target.value as InviteRole })}
                              aria-label={`Row ${index + 1} member type`}
                              className={`${CELL_INPUT} bg-white`}
                            >
                              <option value="member">Member</option>
                              <option value="advisor">Advisor</option>
                              <option value="guest">Guest</option>
                            </select>
                          )}
                        </td>
                        <td className="px-1 py-1 border-l border-gray-100">
                          <select
                            value={row.snapshotType}
                            onChange={(e) =>
                              updateRow(row.key, {
                                snapshotType: e.target.value as InviteSnapshotType,
                              })
                            }
                            aria-label={`Row ${index + 1} snapshot type`}
                            className={`${CELL_INPUT} bg-white`}
                          >
                            <option value="member">Member</option>
                            <option value="advisor">Advisor</option>
                            <option value="leader">Leader</option>
                            <option value="nonprofit">Nonprofit</option>
                          </select>
                        </td>
                        <td className="px-1 py-1 border-l border-gray-100">
                          {isAdmin && row.role === 'admin' ? (
                            <span className="block px-2 py-2 text-sm text-gray-300">—</span>
                          ) : (
                            <select
                              value={row.forumId}
                              onChange={(e) =>
                                updateRow(row.key, { forumId: e.target.value ? Number(e.target.value) : '' })
                              }
                              aria-label={`Row ${index + 1} Forum`}
                              className={`${CELL_INPUT} bg-white`}
                            >
                              <option value="">
                                {isAdmin && row.role !== 'guest' ? 'No Forum yet' : 'Select…'}
                              </option>
                              {forums.map((f) => (
                                <option key={f.id} value={f.id}>
                                  {f.name} ({f.city}, {f.state})
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="px-1 py-1 border-l border-gray-100">
                          {row.role === 'guest' ? (
                            <div className="space-y-1 py-1">
                              <input
                                type="datetime-local"
                                value={row.guestExpiresAt}
                                onChange={(e) => updateRow(row.key, { guestExpiresAt: e.target.value })}
                                aria-label={`Row ${index + 1} guest access expires`}
                                title="When this guest login ends (default 30 days)"
                                className={`${CELL_INPUT}`}
                              />
                              <label className="flex items-start gap-1.5 px-2 text-xs text-gray-600 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={row.skipOnboarding}
                                  onChange={(e) => updateRow(row.key, { skipOnboarding: e.target.checked })}
                                  className="mt-0.5 rounded border-gray-300 text-navy focus:ring-gold"
                                />
                                <span>Skip onboarding (in-meeting)</span>
                              </label>
                            </div>
                          ) : row.role === 'member' || row.role === 'advisor' ? (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.customAmount}
                              onChange={(e) => updateRow(row.key, { customAmount: e.target.value })}
                              placeholder="Select a Forum"
                              aria-label={`Row ${index + 1} monthly amount`}
                              title="Monthly dues. Auto-filled from the Forum and role; edit for a custom or $0 amount."
                              className={`${CELL_INPUT} placeholder:text-gray-300`}
                            />
                          ) : (
                            <span className="block px-2 py-2 text-sm text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  type="button"
                  onClick={addRow}
                  className="w-full flex items-center justify-center gap-2 py-2 border-t border-gray-100 text-sm font-medium text-gray-500 hover:text-navy hover:bg-off-white/60 transition"
                >
                  <Plus size={15} />
                  Add row
                </button>
              </div>

              {rowErrors.length > 0 && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm space-y-1">
                  {rowErrors.map((e) => (
                    <p key={e.index} className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Row {e.index}: {e.message}</span>
                    </p>
                  ))}
                </div>
              )}

              <p className="text-xs text-gray-500">
                <span className="font-medium">$ / mo</span> fills in when you pick a Forum and
                role (member vs advisor). Edit it to charge a different amount, or $0 for no
                charge. They only ever see and pay that amount. Leave the auto-filled number
                to charge the standard rate.
                {anyFacilitatorRole && (
                  <>
                    {' '}Facilitator rows with a Forum are added as that Forum's
                    facilitator; leave the Forum blank and they can create their own
                    Forum after signing in.
                  </>
                )}
              </p>

              {/* Personal note quoted in the invite email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="invite-personal-note">
                  Personal note <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  id="invite-personal-note"
                  value={personalNote}
                  onChange={(e) => setPersonalNote(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="e.g. Excited to have you join our Tuesday forum — see you at the next meeting!"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gold focus:border-gold placeholder:text-gray-300"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Included in the invitation email as a quoted message from you
                  {rows.length > 1 ? ' (everyone in this batch gets the same note)' : ''}.
                </p>
              </div>

              {/* Schedule for later */}
              <div className="border border-gray-200 rounded-lg p-3">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={scheduleEnabled}
                    onChange={(e) => setScheduleEnabled(e.target.checked)}
                    className="rounded border-gray-300 text-navy focus:ring-gold"
                  />
                  <CalendarClock size={16} className="text-gray-500" />
                  Schedule for later
                </label>
                {scheduleEnabled && (
                  <div className="mt-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Send the invitation email{rows.length > 1 ? 's' : ''} at
                    </label>
                    <DateTimeField
                      value={sendAt}
                      min={nextGridLocalDateTime()}
                      stepMinutes={10}
                      onChange={(next) => setSendAt(snapLocalDateTimeForward(next))}
                      aria-label="Send the invitation email at"
                      className="w-full sm:w-auto"
                      inputClassName="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Emails go out on the :00 / :10 / :20 / :30 / :40 / :50 — pick one of
                      those so they send on the dot. Accounts are created when the email
                      goes out. You can edit or cancel from the Invites tab until then.
                    </p>
                  </div>
                )}
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-navy text-white py-2.5 rounded-lg font-semibold hover:bg-navy-dark transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : scheduleEnabled ? (
                    `Schedule ${inviteCount > 1 ? `${inviteCount} Invites` : 'Invite'}`
                  ) : (
                    `Send ${inviteCount > 1 ? `${inviteCount} Invites` : 'Invite'}`
                  )}
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
